import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  BedDouble,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Coffee,
  CreditCard,
  Info,
  MapPin,
  Mountain,
  ShieldCheck,
  Star,
  UtensilsCrossed,
  Users,
  Wifi,
  Loader2,
  AlertCircle,
  ClipboardList,
  DollarSign,
  Key,
  BookOpen,
} from "lucide-react";
import hotelApi from "../../../services/hotelSearchApi";
import { useAuth } from "../../../AuthProvider/AuthContext";
import Login from "../../Main/Login/Login";
import Signup from "../../Main/Signup/Signup";
import { BookingModal } from "./HotelPage";
import "./Hotelbooking.css";

const fmt = (value) => {
  const amount = Number(value || 0);

  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const getRateAmount = (rate) => {
  if (!rate) return 0;

  return Number(
    rate.totalRate ||
    rate.totalAmount ||
    rate.totalPrice ||
    rate.finalPrice ||
    rate.amount ||
    0
  );
};

const getCustomerRateAmount = (rate) => {
  const customerPrice = Number(rate?.customerPrice);

  return Number.isFinite(customerPrice) && customerPrice > 0
    ? customerPrice
    : getRateAmount(rate);
};

const todayStr = () => new Date().toISOString().split("T")[0];
const addDays = (dateStr, days) => {
  const d = new Date(dateStr && dateStr.length >= 8 ? dateStr : Date.now());
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};
const calcNights = (checkIn, checkOut) => {
  try {
    const diff = Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000);
    return diff > 0 ? diff : 1;
  } catch {
    return 1;
  }
};

const DEFAULT_SEARCH_META = {
  checkIn: "",
  checkOut: "",
  occupancies: [{ adultCount: 2, childCount: 0, childAges: [] }],
  countryOfResidence: "IN",
  travelType: 0,
};

const AMENITY_ITEMS = [
  ["Restaurant", UtensilsCrossed],
  ["Gym", Users],
  ["Spa", Coffee],
  ["Bar", Coffee],
  ["Free Wi-Fi", Wifi],
  ["Newspaper", CheckCircle2],
  ["Parking", MapPin],
  ["Wi-Fi", Wifi],
  ["Free toiletries", CheckCircle2],
  ["Ironing facilities", CheckCircle2],
  ["Parking facility", MapPin],
  ["Luggage Storage", BriefcaseBusiness],
  ["Heater-On request", Coffee],
  ["Front Desk", Users],
  ["Newspaper in lobby", CheckCircle2],
  ["Wake-up service", CalendarDays],
  ["Lawns/Gardens", Mountain],
  ["Business Centre", BriefcaseBusiness],
  ["Conference Hall", Users],
  ["Executive Lounge", Coffee],
  ["Room service", Coffee],
  ["Doctor on call", ShieldCheck],
  ["Travel Desk", BriefcaseBusiness],
  ["Central AC", CheckCircle2],
  ["Fire Extinguishers", ShieldCheck],
  ["Paid Pick up/ Drop", MapPin],
  ["Airport Transfer", MapPin],
  ["CCTV", ShieldCheck],
  ["Coffee Shop", Coffee],
  ["Security [24-hour]", ShieldCheck],
  ["Credit/Debit cards", CreditCard],
  ["Elevator", CheckCircle2],
  ["Massage Centre", Coffee],
  ["Games Room", Users],
  ["Laundry", CheckCircle2],
];

function buildAboutSections(hotel) {
  return [
    {
      title: "Property Location",
      icon: MapPin,
      body: hotel.addressLines?.length
        ? hotel.addressLines.join(", ")
        : `Located in ${hotel.location || "the area"}.`,
    },
    {
      title: "Room details",
      icon: BedDouble,
      body: hotel.rooms?.length
        ? `${hotel.rooms.length} room type${hotel.rooms.length !== 1 ? "s" : ""} available. First option: ${hotel.rooms[0].name || "Standard room"}.`
        : "Room and rate details are available for your selected stay.",
    },
  ];
}

// ── HTML-safe renderer ─────────────────────────────────────────────────────────
function HtmlContent({ html, className = "" }) {
  if (!html || typeof html !== "string") return null;
  const isHtml = /<[a-z][\s\S]*>/i.test(html);
  if (isHtml) {
    return (
      <div
        className={`hb-html-content ${className}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return <p className={`hb-plain-text ${className}`}>{html}</p>;
}

// ── Collapsible policy card ────────────────────────────────────────────────────
function PolicyCard({ icon: Icon, title, content, accent = "#0f766e", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!content) return null;

  return (
    <div className="hb-policy-card" style={{ "--policy-accent": accent }}>
      <button
        type="button"
        className="hb-policy-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="hb-policy-icon-wrap">
          <Icon size={18} />
        </span>
        <span className="hb-policy-title">{title}</span>
        <span className="hb-policy-chevron">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && (
        <div className="hb-policy-body">
          <HtmlContent html={content} />
        </div>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <main className="hb-page">
      <div className="hb-loader-banner">
        <div className="hb-loader-icon-wrap">
          <BedDouble size={22} className="hb-loader-icon" />
        </div>
        <div>
          <div className="hb-loader-title">Getting your room ready…</div>
          <div className="hb-loader-sub">Fetching live rates and availability</div>
        </div>
      </div>

      <section className="hb-hero">
        <div className="hb-hero-grid">
          <div className="hb-image-wrap hb-skel shimmer" style={{ minHeight: 280, borderRadius: 12 }} />
          <div className="hb-hero-copy">
            <div className="hb-skel shimmer" style={{ height: 30, width: "75%", borderRadius: 6, marginBottom: 10 }} />
            <div className="hb-skel shimmer" style={{ height: 16, width: "45%", borderRadius: 6, marginBottom: 16 }} />
            <div className="hb-skel shimmer" style={{ height: 14, width: "90%", borderRadius: 6, marginBottom: 8 }} />
            <div className="hb-skel shimmer" style={{ height: 14, width: "60%", borderRadius: 6 }} />
          </div>
          <div className="hb-hero-booking">
            <div className="hb-skel shimmer" style={{ height: 20, width: "50%", borderRadius: 6, marginBottom: 12 }} />
            <div className="hb-skel shimmer" style={{ height: 34, width: "70%", borderRadius: 6, marginBottom: 16 }} />
            <div className="hb-skel shimmer" style={{ height: 44, width: "100%", borderRadius: 10 }} />
          </div>
        </div>
      </section>

      <section className="hb-content">
        <div className="hb-content-grid">
          <div className="hb-main">
            {[1, 2, 3].map((i) => (
              <div className="hb-panel" key={i}>
                <div className="hb-skel shimmer" style={{ height: 20, width: 180, borderRadius: 6, marginBottom: 16 }} />
                <div className="hb-skel shimmer" style={{ height: 14, width: "100%", borderRadius: 6, marginBottom: 8 }} />
                <div className="hb-skel shimmer" style={{ height: 14, width: "85%", borderRadius: 6, marginBottom: 8 }} />
                <div className="hb-skel shimmer" style={{ height: 14, width: "60%", borderRadius: 6 }} />
              </div>
            ))}
          </div>
          <aside className="hb-sticky-sidebar">
            <div className="hb-panel hb-sidebar-card">
              <div className="hb-skel shimmer" style={{ height: 18, width: "70%", borderRadius: 6, marginBottom: 10 }} />
              <div className="hb-skel shimmer" style={{ height: 14, width: "50%", borderRadius: 6, marginBottom: 16 }} />
              <div className="hb-skel shimmer" style={{ height: 30, width: "60%", borderRadius: 6, marginBottom: 16 }} />
              <div className="hb-skel shimmer" style={{ height: 44, width: "100%", borderRadius: 10 }} />
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

function ErrorState({ message, onBack }) {
  return (
    <main className="hb-page" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 16 }}>
      <AlertCircle size={48} color="#ef4444" />
      <h2 style={{ color: "#ef4444" }}>Failed to load hotel</h2>
      <p style={{ color: "#6b7280" }}>{message}</p>
      <button type="button" className="hb-hero-book-btn" onClick={onBack}>
        ← Back to hotels
      </button>
    </main>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function HotelBooking() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [selectedRates, setSelectedRates] = useState({});
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingHotel, setBookingHotel] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const searchMeta = (() => {
    try {
      const raw = sessionStorage.getItem("hotelSearchMeta");
      const stored = raw ? JSON.parse(raw) : {};
      const query = new URLSearchParams(location.search);
      const queryOccupancies = query.get("occupancies") ? JSON.parse(query.get("occupancies")) : null;
      const resolvedCountry = stored.countryOfResidence || query.get("country") || DEFAULT_SEARCH_META.countryOfResidence;

      return {
  ...DEFAULT_SEARCH_META,
  ...stored,

  requestId:
    stored.requestId ||
    query.get("requestId") ||
    "",

  searchKey:
    stored.searchKey ||
    query.get("searchKey") ||
    "",

  locationId:
    stored.locationId ||
    query.get("locationId") ||
    "",

  checkIn:
    stored.checkIn ||
    query.get("checkIn") ||
    "",

  checkOut:
    stored.checkOut ||
    query.get("checkOut") ||
    "",

  occupancies:
  Array.isArray(queryOccupancies) &&
  queryOccupancies.length > 0
    ? queryOccupancies
    : Array.isArray(stored.occupancies) &&
      stored.occupancies.length > 0
      ? stored.occupancies
      : DEFAULT_SEARCH_META.occupancies,

  countryOfResidence: resolvedCountry,

  travelType:
    stored.travelType ??
    (
      query.get("travelType") !== null
        ? Number(query.get("travelType"))
        : resolvedCountry === "IN"
          ? 0
          : 1
    ),
};
    } catch {
      return DEFAULT_SEARCH_META;
    }
  })();

  useEffect(() => {
    if (!hotelId) {
      setError("No hotel ID provided.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchHotelDetails() {
      setLoading(true);
      setError(null);

      const requestedRoomCount =
  Number(searchMeta.rooms) ||
  Number(searchMeta.occupancies?.length) ||
  1;

const rawOccupancies =
  Array.isArray(searchMeta.occupancies)
    ? searchMeta.occupancies
    : [];

const totalAdults =
  Number(searchMeta.adults) ||
  rawOccupancies.reduce(
    (sum, o) => sum + Number(o.adultCount || 0),
    0
  ) ||
  requestedRoomCount;

const totalChildren =
  Number(searchMeta.children) ||
  rawOccupancies.reduce(
    (sum, o) => sum + Number(o.childCount || 0),
    0
  );

const allChildAges =
  Array.isArray(searchMeta.childAges) &&
  searchMeta.childAges.length
    ? searchMeta.childAges.map(Number)
    : rawOccupancies.flatMap(
        (o) =>
          Array.isArray(o.childAges)
            ? o.childAges.map(Number)
            : []
      );

const resolvedOccupancies =
  rawOccupancies.length === requestedRoomCount
    ? rawOccupancies.map((o) => ({
        adultCount: Number(o.adultCount || 0),
        childCount: Number(o.childCount || 0),
        childAges: Array.isArray(o.childAges)
          ? o.childAges.map(Number)
          : [],
      }))
    : Array.from(
        { length: requestedRoomCount },
        (_, index) => {
          const baseAdults =
            Math.floor(totalAdults / requestedRoomCount);

          const extraAdults =
            index < totalAdults % requestedRoomCount
              ? 1
              : 0;

          const baseChildren =
            Math.floor(totalChildren / requestedRoomCount);

          const extraChildren =
            index < totalChildren % requestedRoomCount
              ? 1
              : 0;

          const childCount =
            baseChildren + extraChildren;

          const previousChildren =
            baseChildren * index +
            Math.min(
              index,
              totalChildren % requestedRoomCount
            );

          return {
            adultCount: baseAdults + extraAdults,
            childCount,
            childAges: allChildAges.slice(
              previousChildren,
              previousChildren + childCount
            ),
          };
        }
      );

      const requestPayload = {
  requestId: searchMeta.requestId || "",
  hotelId: searchMeta.encodedHotelId || hotelId,
        searchKey: searchMeta.searchKey || "",
        checkIn: searchMeta.checkIn || addDays(todayStr(), 1),
        checkOut: searchMeta.checkOut || addDays(todayStr(), 2),
        occupancies: resolvedOccupancies,
        countryOfResidence: searchMeta.countryOfResidence || DEFAULT_SEARCH_META.countryOfResidence,
        travelType: searchMeta.travelType ?? DEFAULT_SEARCH_META.travelType,
      };

      let finalRequestPayload = { ...requestPayload };

      if (searchMeta.locationId) {
        try {
          const freshSearch = await hotelApi.search({
  locationId: searchMeta.locationId,
  checkIn: requestPayload.checkIn,
  checkOut: requestPayload.checkOut,
  occupancies: requestPayload.occupancies,
  countryOfResidence: requestPayload.countryOfResidence,
  travelType: requestPayload.travelType,
  requestId: searchMeta.requestId || undefined,
});

if (cancelled) return;

const freshStatus = String(
  freshSearch?.meta?.status || ""
).toUpperCase();

if (freshStatus && freshStatus !== "SUCCESS") {
  throw new Error(
    `Hotel search is not ready yet (${freshStatus})`
  );
}

const freshSearchKey =
  freshSearch?.searchKey ||
  freshSearch?.search_key ||
  "";

const freshHotels = Array.isArray(freshSearch?.hotels)
  ? freshSearch.hotels
  : [];
          const matchedHotel =
            freshHotels.find(
              (item) =>
                String(item.hotelId || "") ===
                String(searchMeta.encodedHotelId || hotelId)
            ) ||
            freshHotels.find(
              (item) =>
                String(item.hotelName || "")
                  .trim()
                  .toLowerCase() ===
                String(location.state?.hotelName || "")
                  .trim()
                  .toLowerCase()
            );

          if (freshSearchKey) {
            finalRequestPayload.searchKey = freshSearchKey;
          }

          if (matchedHotel?.hotelId) {
            finalRequestPayload.hotelId = matchedHotel.hotelId;
          }
        } catch (err) {
  console.error(
    "[HotelBooking] fresh search failed:",
    err.message || err
  );

  if (!cancelled) {
    setError(
      "Live hotel availability could not be refreshed. Please try again."
    );
    setLoading(false);
  }

  return;
}
      }

      if (cancelled) return;

      

let data;
try {
  data = await hotelApi.details(finalRequestPayload);
      } catch (firstErr) {
        console.warn("[HotelBooking] /details failed, retrying once:", firstErr.message || firstErr);
        try {
          data = await hotelApi.details(finalRequestPayload);
        } catch (secondErr) {
          if (!cancelled) {
            console.error("[HotelBooking] fetchHotelDetails failed after retry:", secondErr);
            setError(secondErr.message || "Unable to load hotel details. Please try again.");
            setLoading(false);
          }
          return;
        }
      }

      try {
        if (cancelled) return;

        const responseHotel = data?.hotel || {};
        console.log("🔑 DETAILS RAW:", JSON.stringify({
          topLevelHotelId: data?.hotelId,
          topLevelOnHold: data?.onHold,
          searchKey: data?.searchKey,
        }, null, 2));
        // ── Image extraction ──────────────────────────────────────────────────
        const extractImage = (src) => {
          if (!src) return "";
          if (typeof src === "string") return src;
          if (typeof src === "object") return src.url || src.imageUrl || src.src || "";
          return "";
        };

        const room = responseHotel.rooms?.[0] || {};
        const hotelImagesObj = responseHotel.hotelImages || {};

        const totalImageCount = Object.values(hotelImagesObj).reduce(
          (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0
        );
        const primaryImage =
          hotelImagesObj["Primary image"]?.[0] ||
          hotelImagesObj["Property entrance"]?.[0] ||
          hotelImagesObj["Exterior"]?.[0] ||
          Object.values(hotelImagesObj).find(arr => Array.isArray(arr) && arr.length > 0)?.[0] ||
          "";

        const rawImages = [
          responseHotel.mainImage,
          responseHotel.main_image,
          responseHotel.imageUrl,
          responseHotel.image_url,
          responseHotel.thumbnailUrl,
          responseHotel.thumbnail,
          primaryImage,           // ✅ extracted from the object above
          room.image,
          room.roomImage,
          room.images?.[0],
        ];

        const image = rawImages.map(extractImage).find(Boolean) || "";

        // ── Debug (remove once image shows correctly) ─────────────────────────
        console.log("🖼️ IMAGE DEBUG:", {
          resolvedImage: image,
          keys: Object.keys(responseHotel),
          images: responseHotel.images,
          photos: responseHotel.photos,
        });

        // ── Address ───────────────────────────────────────────────────────────
        const address = responseHotel.contact?.address || {};
        const city = address.city?.name || responseHotel.cityName || responseHotel.location || "";
        const country = address.country?.name || address.country?.code || "";
        const lines = [address.line1, address.line2].filter(Boolean);

        const checkIn = data?.checkInDate || searchMeta.checkIn || addDays(todayStr(), 1);
        const checkOut = data?.checkOutDate || searchMeta.checkOut || addDays(todayStr(), 2);
        const nights = calcNights(checkIn, checkOut);
        const allRates =
          (responseHotel.rooms || [])
            .flatMap((room) => room.rates || []);

        const firstPricedRate =
          allRates.find((rate) => getRateAmount(rate) > 0) || {};

        const rate =
          getRateAmount(room.rates?.[0]) > 0
            ? room.rates[0]
            : firstPricedRate;

        
const totalRate = getRateAmount(rate);
const displayRate = getCustomerRateAmount(rate);

        // ── Policies ──────────────────────────────────────────────────────────
        const hotelPolicies = responseHotel.policies || responseHotel.hotelPolicies || {};
        const CANCEL_KEYS = ["cancellation", "cancellationpolicy", "cancellationpolicies", "refund", "refundpolicy"];
        const ratePolicies = (rate.policies || []).filter(
          (p) => !CANCEL_KEYS.includes(p.type?.toLowerCase().replace(/\s+/g, ""))
        );

        const findPolicy = (key) => {
          if (hotelPolicies[key]) return hotelPolicies[key];
          const match = ratePolicies.find(
            (p) => p.type?.toLowerCase().replace(/\s+/g, "") === key.toLowerCase().replace(/\s+/g, "")
          );
          if (match) return match.text;
          if (responseHotel[key]) return responseHotel[key];
          return null;
        };

        const checkInInstruction = findPolicy("checkInInstruction") || findPolicy("checkInInstructions") || findPolicy("checkin_instruction") || responseHotel.checkInInstruction || data?.checkInInstruction || null;
        const specialCheckIn = findPolicy("specialCheckInInstruction") || findPolicy("specialCheckinInstruction") || responseHotel.specialCheckInInstruction || data?.specialCheckInInstruction || null;
        const knowBeforeYouGo = findPolicy("knowBeforeYouGo") || findPolicy("know_before_you_go") || responseHotel.knowBeforeYouGo || data?.knowBeforeYouGo || null;
        const optionalFee = findPolicy("optionalFee") || findPolicy("optionalFees") || findPolicy("fees") || responseHotel.optionalFee || data?.optionalFee || null;
        const cribsExtraBeds = findPolicy("cribsExtraBeds") || findPolicy("cribs_extra_beds") || findPolicy("extraBeds") || responseHotel.cribsExtraBeds || data?.cribsExtraBeds || null;
        const guestBookingNotes = findPolicy("guestBookingNotes") || findPolicy("guestNotes") || findPolicy("bookingNotes") || responseHotel.guestBookingNotes || data?.guestBookingNotes || null;

        setHotel({
          id: hotelId, // display/routing id — do NOT feed this back to /details
          encodedHotelId: searchMeta.encodedHotelId || data?.hotelId || data?.hotel_id || responseHotel.id || hotelId,
          name: responseHotel.name || "Hotel",
          subtitle: responseHotel.chainName || responseHotel.chain_name || "",
          rating: Number(responseHotel.userRating || responseHotel.user_rating || responseHotel.guestRating || responseHotel.rating || responseHotel.reviewRating || responseHotel.score || 0),
          ratingLabel: responseHotel.ratingLabel || responseHotel.rating_label || "",
          ratingCount: Number(responseHotel.reviewCount || responseHotel.review_count || responseHotel.ratingCount || responseHotel.totalReviews || responseHotel.total_reviews || responseHotel.reviews || 0),
          discount: responseHotel.discount || responseHotel.discountText || responseHotel.offer_text || "",
          originalPrice: Number(room.originalPrice || responseHotel.originalPrice || responseHotel.original_price || 0),
          starRating: Number(responseHotel.starRating || responseHotel.star_rating || 0),
          stars: Math.round(
            Number(
              responseHotel.starRating ||
              responseHotel.star_rating ||
              0
            )
          ),
          checkInTime: responseHotel.checkInTime || responseHotel.check_in_time || "",
          checkOutTime: responseHotel.checkOutTime || responseHotel.check_out_time || "",
          location: city,
          locationDetail: lines.length > 0 ? `${lines.join(", ")}, ${country}` : country,
          addressLines: [...lines, city, country].filter(Boolean),
          contactPhones: responseHotel.contact?.phones || [],
          contactEmails: responseHotel.contact?.emails || [],
          website: responseHotel.contact?.website || "",
          amenities: responseHotel.amenities?.map(a => a.name) || [],
          facilities: responseHotel.facilities?.map(f => f.name) || [],
          nearByAttractions: responseHotel.nearByAttractions || [],
          rooms: responseHotel.rooms || [],
          price: displayRate,
totalPrice: displayRate,
supplierPrice: totalRate,
          baseRate: Number(rate.baseRate || 0),
          taxes: Number(rate.totalTax || 0),
          netFare: Number(rate.netFare || 0),
          rateName: rate.name || "",
          rateType: rate.type || "",
          refundable: rate.refundable ?? false,
          refundability: rate.refundability || "",
          onHold: rate.onHold ?? responseHotel.onHold ?? data?.onHold ?? false,
          noOfRoomsLeft: rate.noOfRoomsLeft ?? null,
          cancellationPolicies: rate.cancellationPolicies || [],
          policies: ratePolicies,
          searchKey: data?.searchKey || searchMeta.searchKey || "",
          checkIn,
          checkOut,
          occupancies: data?.occupancies || searchMeta.occupancies || DEFAULT_SEARCH_META.occupancies,
          image, // ✅ clean single variable
          checkInInstruction,
          specialCheckIn,
          knowBeforeYouGo,
          optionalFee,
          cribsExtraBeds,
          guestBookingNotes,
        });
      } catch (err) {
        if (!cancelled) {
          console.error("[HotelBooking] fetchHotelDetails:", err);
          setError(err.message || "Unable to load hotel details. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchHotelDetails();
    return () => { cancelled = true; };
  }, [hotelId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.hash === "#hotel-policies") {
      setTimeout(() => {
        document.getElementById("hotel-policies")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [location.hash, hotel]);

  const handleBookNow = () => {
    document.getElementById("rooms-rates")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorState message={error} onBack={() => navigate("/hotels")} />;
  if (!hotel) return <ErrorState message="Hotel not found." onBack={() => navigate("/hotels")} />;

  const aboutSections = buildAboutSections(hotel);
  const stayType =
  Number(searchMeta.travelType) === 1
    ? "International"
    : "Domestic";

  // Check if any policy data exists
  const hasPolicies = [
    hotel.checkInInstruction,
    hotel.specialCheckIn,
    hotel.knowBeforeYouGo,
    hotel.optionalFee,
    hotel.cribsExtraBeds,
    hotel.guestBookingNotes,
  ].some(Boolean);
  const getSelectedRateIndex = (roomKey) => selectedRates[roomKey] ?? 0;
  return (
    <main className="hb-page">
      {bookingHotel && (
        <BookingModal
          hotel={bookingHotel}
          searchMeta={searchMeta}
          onClose={() => setBookingHotel(null)}
          token={token}
          user={user}
        />
      )}
      {showLoginModal && (
        <Login
          onClose={() => setShowLoginModal(false)}
          onSwitchToSignup={() => { setShowLoginModal(false); setShowSignupModal(true); }}
          onLoginSuccess={() => { setShowLoginModal(false); setBookingHotel(hotel); }}
        />
      )}
      {showSignupModal && (
        <Signup
          onClose={() => setShowSignupModal(false)}
          onSwitchToLogin={() => { setShowSignupModal(false); setShowLoginModal(true); }}
        />
      )}

      {/* ── Hero ── */}
      <section className="hb-hero">

        <div className="hb-hero-grid">
          <div className="hb-image-wrap">
            {hotel.image ? (
              <img src={hotel.image} alt={hotel.name} />
            ) : (
              <div style={{ background: "#e5e7eb", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Mountain size={48} color="#9ca3af" />
              </div>
            )}
            {hotel.totalImageCount > 1 && (
              <span className="hb-photo-pill">{hotel.totalImageCount} Photos</span>
            )}
            <button type="button" className="hb-heart-btn">♡</button>
          </div>

          <div className="hb-hero-copy">
            <h1>{hotel.name}</h1>
            {hotel.subtitle && <p className="hb-subtitle">{hotel.subtitle}</p>}
            {hotel.rating > 0 && (
              <div className="hb-rating-row">
                <span className="hb-stars">
                  <Star size={13} fill="currentColor" />
                </span>

                <span className="hb-score">{hotel.rating}</span>

                {hotel.ratingLabel && (
                  <span>{hotel.ratingLabel}</span>
                )}

                {hotel.ratingCount > 0 && (
                  <span>
                    ({hotel.ratingCount.toLocaleString("en-IN")} reviews)
                  </span>
                )}
              </div>
            )}
            <div className="hb-hero-facts">
  <span>
    <MapPin size={16} />
    {hotel.location || "Location unavailable"}
  </span>

  <span>
    <MapPin size={16} />
    {stayType} Stay
  </span>

  {hotel.checkInTime && (
    <span>
      <CalendarDays size={16} />
      Check-in {hotel.checkInTime}
    </span>
  )}

  {hotel.checkOutTime && (
    <span>
      <CalendarDays size={16} />
      Check-out {hotel.checkOutTime}
    </span>
  )}

  {hotel.noOfRoomsLeft > 0 && (
    <span>
      <Users size={16} />
      {hotel.noOfRoomsLeft} rooms left
    </span>
  )}

  <span>
    <BedDouble size={16} />
    {hotel.rateName ||
      hotel.rateType ||
      "Room rate details"}
  </span>
</div>
          </div>

          <div className="hb-hero-booking">
            <div>
              {hotel.originalPrice > 0 && (
                <span className="hb-hero-old-price">{fmt(hotel.originalPrice)}</span>
              )}
              <div className="hb-hero-price">
                <strong>
                  {hotel.totalPrice > 0
                    ? fmt(hotel.totalPrice)
                    : "Choose a room below"}
                </strong>
              </div>

              {hotel.totalPrice > 0 && (
                <span className="hb-hero-price-label">
                  Total stay
                </span>
              )}
            </div>
            <button type="button" className="hb-hero-book-btn" onClick={handleBookNow}>
              CHOOSE ROOM
            </button>
          </div>

          <div className="hb-hero-offer">
            <span>BANK</span>
            <strong>⚡</strong>
            {hotel.discount || "Lightning Drop — Save ₹5000 Today Only!"}
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="hb-content">
        <div className="hb-content-grid">
          <div className="hb-main">

            {/* About panel */}
            <div className="hb-panel hb-about-panel">
              <div className="hb-about-header">
                <span className="hb-about-logo"><Mountain size={25} /></span>
                <div>
                  <div className="hb-about-title-row">
                    <h2>About {hotel.name}</h2>
                    <span className="hb-about-stars">
                      {Array.from({ length: hotel.stars }).map((_, i) => (
                        <Star key={i} size={14} fill="currentColor" />
                      ))}
                    </span>
                  </div>
                  <p className="hb-about-location">
                    <MapPin size={13} />
                    {hotel.locationDetail || hotel.location}
                  </p>
                </div>
              </div>
              <div className="hb-about-copy">
                {aboutSections.map(({ title, icon: Icon, body }) => (
                  <div className="hb-about-row" key={title}>
                    <span className="hb-about-row-icon"><Icon size={21} /></span>
                    <div>
                      <h3>{title}</h3>
                      <p>{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="hb-panel">
              <h2>Included Benefits</h2>
              <div className="hb-benefits">
                <span><ShieldCheck size={18} /> Secure booking</span>
                <span><CheckCircle2 size={18} /> Free cancellation</span>
                <span><CreditCard size={18} /> Pay at hotel available</span>
                <span><Wifi size={18} /> Wi-Fi and essentials</span>
              </div>
            </div>

            {/* Rooms & Rates */}
            <div className="hb-panel" id="rooms-rates">
              <h2>ROOMS & RATES</h2>
              {hotel.rooms?.length > 0 ? (
                hotel.rooms.map((room) => {
                  const roomKey = room.id || room.name;
                  const rateIndex = getSelectedRateIndex(roomKey);
                  const rates = room.rates?.length > 0 ? room.rates : [];
                  const activeRate = rates[rateIndex] || rates[0] || {};
                  const activeRateAmount = getCustomerRateAmount(activeRate);
                  return (
                    <div
                      className="hb-room-card"
                      key={roomKey}
                    >
                      <div className="hb-room-card-head">
                        <div>
                          <div className="hb-room-name">{room.name || "Room"}</div>
                          <div className="hb-room-meta">
                            {room.roomSize && <span>{room.roomSize}</span>}
                            {room.beds?.length > 0 && (
                              <span>{room.beds.map(b => `${b.count} ${b.type}`).join(", ")}</span>
                            )}
                          </div>
                        </div>
                        <div className="hb-room-rate">
                          <div className="hb-room-rate-total">
                            {activeRateAmount > 0
                              ? fmt(activeRateAmount)
                              : "Check live rate"}
                          </div>
                          <div className="hb-room-rate-pernight">{activeRate.type || "Rate details"}</div>
                        </div>
                      </div>

                      {rates.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, margin: "12px 20px" }}>
                          {rates.map((rate, idx) => {
                            const isActive = idx === rateIndex;
                            return (
                              <button
                                key={rate.id || idx}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRates(prev => ({ ...prev, [roomKey]: idx }));
                                }}
                                style={{
                                  textAlign: "left",
                                  padding: "10px 14px",
                                  borderRadius: 10,
                                  border: isActive ? "2px solid #0f766e" : "1px solid #e2e8f0",
                                  background: isActive ? "#ecfdf5" : "#fff",
                                  cursor: "pointer",
                                  minWidth: 180,
                                }}
                              >
                                <div style={{ fontWeight: 700, fontSize: 13, color: "#0d1b3e" }}>
                                  {rate.name || rate.type || `Option ${idx + 1}`}
                                </div>
                                <div style={{ fontSize: 13, color: "#0f766e", fontWeight: 700, marginTop: 4 }}>
                                  {getCustomerRateAmount(rate) > 0
  ? fmt(getCustomerRateAmount(rate))
  : "Check live rate"}
                                </div>
                                <div style={{ fontSize: 11, color: rate.refundable ? "#059669" : "#b91c1c", marginTop: 2 }}>
                                  {rate.refundable ? "Free cancellation" : "Non-refundable"}
                                </div>
                                {rate.noOfRoomsLeft > 0 && rate.noOfRoomsLeft <= 5 && (
                                  <div style={{ fontSize: 11, color: "#b45309", marginTop: 2 }}>
                                    Only {rate.noOfRoomsLeft} left
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {activeRate.id && (
                        Number(activeRate.baseRate || 0) > 0 ||
                        Number(activeRate.totalTax || 0) > 0 ||
                        Number(activeRate.netFare || 0) > 0
                      ) && (
                        <div className="hb-room-rate-details">
                          {Number(activeRate.baseRate || 0) > 0 && (
                            <div>Base: {fmt(activeRate.baseRate)}</div>
                          )}
                          {Number(activeRate.totalTax || 0) > 0 && (
                            <div>Taxes: {fmt(activeRate.totalTax)}</div>
                          )}
                          {Number(activeRate.netFare || 0) > 0 && (
                            <div>Net fare: {fmt(activeRate.netFare)}</div>
                          )}
                        </div>
                      )}

                      
<div className="hb-room-incl-note">
  <ShieldCheck size={14} /> Final customer price is calculated using the current hotel pricing rules at checkout.
</div>


                      {activeRate.cancellationPolicies?.length > 0 && (
                        <div className="hb-room-cancellation-badge">
                          <ShieldCheck size={14} />
                          {activeRate.cancellationPolicies[0].text
                            ?.replace(/<[^>]*>/g, "").trim() || "Cancellation policy applies"}
                        </div>
                      )}

                      <div className="hb-room-book-row">
                        <div className="hb-room-book-price">
                          <span className="hb-room-book-amount">
                            {activeRateAmount > 0
                              ? fmt(activeRateAmount)
                              : "Check live rate"}
                          </span>
                          
<span className="hb-room-book-label">
  Customer price · includes admin charges
</span>

                        </div>
                        <button
                          type="button"
                          className="hb-room-book-btn"
                          
onClick={() => {
  if (!user || !token) {
    setShowLoginModal(true);
    return;
  }

  setBookingHotel({
    ...hotel,

    selectedRoom: {
      ...room,
      id: room.id || roomKey,
      name: room.name || "Room",
    },

    selectedRoomId: room.id || roomKey,
    selectedRoomName: room.name || "Room",

    selectedRate: activeRate,
    selectedRateId:
      activeRate.id ||
      activeRate.rateId ||
      activeRate.rate_id ||
      activeRate.rateKey ||
      activeRate.rate_key ||
      "",

    rateName:
      activeRate.name ||
      room.name ||
      hotel.rateName,

    rateType:
      activeRate.type ||
      hotel.rateType ||
      "",

    price: activeRateAmount > 0
      ? activeRateAmount
      : hotel.price,

    totalPrice: activeRateAmount > 0
      ? activeRateAmount
      : hotel.totalPrice,

    baseRate: Number(activeRate.baseRate || hotel.baseRate),
    taxes: Number(activeRate.totalTax || hotel.taxes),
    netFare: Number(activeRate.netFare || hotel.netFare),

    cancellationPolicies:
      activeRate.cancellationPolicies ||
      hotel.cancellationPolicies,

    rateKey:
      activeRate.id ||
      activeRate.rateKey ||
      activeRate.rate_key ||
      hotel.rateKey,
  });
}}

                        >
                          Book This Room
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p>No room details available for this hotel.</p>
              )}
            </div>

            {/* ── Hotel Policies — NEW SECTION ─────────────────────────────────── */}
            {hasPolicies && (
              <div className="hb-panel hb-policies-panel" id="hotel-policies">
                <div className="hb-policies-header">
                  <span className="hb-policies-icon"><ClipboardList size={20} /></span>
                  <h2>Hotel Policies & Information</h2>
                </div>
                <p className="hb-policies-subtitle">
                  Please review all policies carefully before booking. Some items may require action at check-in.
                </p>

                <div className="hb-policies-grid">
                  <PolicyCard
                    icon={Key}
                    title="Check-in Instructions"
                    content={hotel.checkInInstruction}
                    accent="#0f766e"
                    defaultOpen={true}
                  />
                  <PolicyCard
                    icon={Info}
                    title="Special Check-in Instructions"
                    content={hotel.specialCheckIn}
                    accent="#2563eb"
                  />
                  <PolicyCard
                    icon={BookOpen}
                    title="Know Before You Go"
                    content={hotel.knowBeforeYouGo}
                    accent="#7c3aed"
                    defaultOpen={true}
                  />
                  <PolicyCard
                    icon={DollarSign}
                    title="Optional Fees"
                    content={hotel.optionalFee}
                    accent="#b45309"
                  />
                  <PolicyCard
                    icon={BedDouble}
                    title="Cribs & Extra Beds"
                    content={hotel.cribsExtraBeds}
                    accent="#0369a1"
                  />
                  <PolicyCard
                    icon={ClipboardList}
                    title="Guest & Booking Notes"
                    content={hotel.guestBookingNotes}
                    accent="#be185d"
                  />
                </div>
              </div>
            )}

            {/* Amenities & Facilities */}

            <div className="hb-panel hb-amenities-panel">
              <h2>AMENITIES & FACILITIES</h2>

              {(hotel.amenities?.length > 0 || hotel.facilities?.length > 0) ? (
                <>
                  {hotel.amenities?.length > 0 && (
                    <div className="hb-amenities-section">
                      <div className="hb-amenities-section-title">
                        <Coffee size={15} aria-hidden="true" />
                        Amenities
                      </div>
                      <div className="hb-amenities-grid">
                        {hotel.amenities.map((label) => (
                          <div className="hb-amenity-item" key={`amenity-${label}`}>
                            <span className="hb-amenity-icon"><Coffee size={15} /></span>
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {hotel.facilities?.length > 0 && (
                    <div className="hb-amenities-section">
                      <div className="hb-amenities-section-title">
                        <CheckCircle2 size={15} aria-hidden="true" />
                        Facilities
                      </div>
                      <div className="hb-amenities-grid">
                        {hotel.facilities.map((label) => (
                          <div className="hb-amenity-item" key={`facility-${label}`}>
                            <span className="hb-amenity-icon"><CheckCircle2 size={15} /></span>
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ color: "var(--hb-text-muted)", fontSize: "0.85rem" }}>
                  No amenities or facilities listed.
                </p>
              )}
            </div>

            {/* Nearby Attractions */}
            <div className="hb-panel">
              <h2>NEARBY ATTRACTIONS</h2>
              {hotel.nearByAttractions?.length > 0 ? (
                <div className="hb-attractions-grid">
                  {hotel.nearByAttractions.map((attraction) => (
                    <div className="hb-attraction-card" key={attraction.name}>
                      <span className="hb-attraction-icon">
                        <MapPin size={15} />
                      </span>
                      <div className="hb-attraction-info">
                        <span className="hb-attraction-name">{attraction.name}</span>
                        <span className="hb-attraction-dist">
                          {attraction.distance}{attraction.unit}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "var(--hb-text-muted)", fontSize: "0.85rem" }}>
                  No nearby attractions listed.
                </p>
              )}
            </div>

          </div>
        </div>
      </section>
    </main>
  );
}
